import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CanvasContainer from '../components/CanvasContainer';
import { zonesAPI, camerasAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  ArrowLeft,
  Upload,
  Save,
  Download,
  Undo,
  Trash2,
  Edit3,
  Pencil,
  ZoomIn,
  ZoomOut,
  Grid3x3,
  CheckCircle2,
  Square,
  Circle,
  Triangle,
  Zap,
  AlignJustify,
  FileUp,
  X
} from 'lucide-react';
import {
  DRAW_MODES,
  ZONE_TYPES,
  ZONE_TYPE_LABELS,
  ZONE_TYPE_COLORS,
  ZONE_TYPE_DRAW_MODES,
  LINE_CROSSING_STEPS,
  DEFAULT_SCALE_FACTOR,
  MIN_SCALE_FACTOR,
  MAX_SCALE_FACTOR,
  SCALE_SPEED,
  RADIANS_PER_45_DEGREES,
} from '../utils/constants';
import {
  drawAllPolygons,
  drawCurrentPolygon,
  drawPreviewLine,
  calculateImageScale,
  constrainToAngle,
  drawLineCrossingWorkInProgress,
} from '../utils/canvasUtils';
import {
  loadImageFromFile,
  formatDeepStreamCoordinates,
  exportDeepStreamConfig,
  loadConfigFromFile,
  parseDeepStreamConfig,
} from '../utils/coordinateUtils';

const ZONE_TYPE_ICONS = {
  [ZONE_TYPES.ROI_FILTERING]: Square,
  [ZONE_TYPES.OVERCROWDING]: Circle,
  [ZONE_TYPES.LINE_CROSSING]: Zap,
  [ZONE_TYPES.DIRECTION_DETECTION]: AlignJustify,
};

export default function ZoneEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const configInputRef = useRef(null);

  // Get camera info from URL query params
  const searchParams = new URLSearchParams(location.search);
  const cameraId = searchParams.get('cameraId');
  const [cameraName, setCameraName] = useState('Loading...');
  const [cameraFormId, setCameraFormId] = useState('');

  // Canvas state
  const [scaleFactor, setScaleFactor] = useState(DEFAULT_SCALE_FACTOR);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [cursor, setCursor] = useState('default');
  const [coordinates, setCoordinates] = useState({ x: '', y: '' });
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [imageFile, setImageFile] = useState(null);

  // Drawing state
  const [points, setPoints] = useState([]);
  const [masterPoints, setMasterPoints] = useState([]);
  const [masterColors, setMasterColors] = useState([]);
  const [masterZoneTypes, setMasterZoneTypes] = useState([]);
  const [masterZoneIds, setMasterZoneIds] = useState([]);
  const [currentZoneType, setCurrentZoneType] = useState(ZONE_TYPES.ROI_FILTERING);
  const [drawMode, setDrawMode] = useState(DRAW_MODES.POLYGON);
  const [editMode, setEditMode] = useState(false);
  const [selectedPointIndex, setSelectedPointIndex] = useState(-1);
  const [selectedPolygonIndex, setSelectedPolygonIndex] = useState(-1);
  const [constrainAngles, setConstrainAngles] = useState(false);

  // Line crossing state
  const [lineCrossingStep, setLineCrossingStep] = useState(LINE_CROSSING_STEPS.DIRECTION);
  const [directionLine, setDirectionLine] = useState([]);
  const [crossingLine, setCrossingLine] = useState([]);

  // UI state
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importText, setImportText] = useState('');
  const [selectedZoneIndex, setSelectedZoneIndex] = useState(-1);

  // Redirect if no camera selected and load camera name
  useEffect(() => {
    if (!cameraId) {
      navigate('/cameras');
    } else {
      // Load camera name and camera_id
      camerasAPI.getById(cameraId)
        .then(result => {
          if (result.camera) {
            setCameraName(result.camera.name || 'Unknown Camera');
            setCameraFormId(result.camera.camera_id || cameraId);
          }
        })
        .catch(err => {
          console.error('Failed to load camera:', err);
          setCameraName('Unknown Camera');
          setCameraFormId(cameraId);
        });
    }
  }, [cameraId, navigate]);

  // Load camera image if exists
  useEffect(() => {
    const loadCameraImage = async () => {
      if (!cameraId) return;

      try {
        const camera = await camerasAPI.getById(cameraId);
        if (camera.camera?.image_url) {
          // Load image from URL
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = async () => {
            imgRef.current = img;
            const canvas = canvasRef.current;
            const container = containerRef.current;
            if (!canvas || !container) return;

            const scale = calculateImageScale(img, container.offsetWidth, container.offsetHeight, 0.95);
            setScaleFactor(scale);
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            setImageDimensions({ width: img.width, height: img.height });

            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            setIsImageLoaded(true);
          };
          img.src = camera.camera.image_url;
        }
      } catch (err) {
        console.error('Failed to load camera image:', err);
      }
    };

    loadCameraImage();
  }, [cameraId]);

  // Load existing zones
  useEffect(() => {
    const loadZones = async () => {
      if (!cameraId || !isImageLoaded) return;

      try {
        const result = await zonesAPI.getAll(cameraId);
        if (result.zones && result.zones.length > 0) {
          const newMasterPoints = [];
          const newMasterColors = [];
          const newMasterZoneTypes = [];
          const newMasterZoneIds = [];

          result.zones.forEach(zone => {
            newMasterZoneTypes.push(zone.zone_type);
            newMasterColors.push(ZONE_TYPE_COLORS[zone.zone_type] || '#00FFCE');
            newMasterZoneIds.push(zone.id);

            // Convert normalized coordinates (0-1 ratio) back to pixels
            const imgWidth = imageDimensions.width;
            const imgHeight = imageDimensions.height;

            if (zone.zone_type === ZONE_TYPES.LINE_CROSSING) {
              // Line crossing: denormalize and convert to flat array [x1d, y1d, x2d, y2d, x1c, y1c, x2c, y2c]
              const flatPoints = zone.points.flatMap(p => [p.x * imgWidth, p.y * imgHeight]);
              newMasterPoints.push(flatPoints);
            } else if (zone.zone_type === ZONE_TYPES.DIRECTION_DETECTION) {
              // Direction: denormalize and convert to flat array [x1, y1, x2, y2]
              const flatPoints = zone.points.flatMap(p => [p.x * imgWidth, p.y * imgHeight]);
              newMasterPoints.push(flatPoints);
            } else {
              // ROI Filtering, Overcrowding: denormalize and convert to array of [x, y] pairs
              const pointPairs = zone.points.map(p => [p.x * imgWidth, p.y * imgHeight]);
              newMasterPoints.push(pointPairs);
            }
          });

          setMasterPoints(newMasterPoints);
          setMasterColors(newMasterColors);
          setMasterZoneTypes(newMasterZoneTypes);
          setMasterZoneIds(newMasterZoneIds);

          // Trigger a redraw after a short delay to ensure canvas is ready
          setTimeout(() => {
            const canvas = canvasRef.current;
            const img = imgRef.current;
            if (canvas && img) {
              const ctx = canvas.getContext('2d');
              drawAllPolygons(ctx, img, newMasterPoints, newMasterColors, newMasterZoneTypes);
            }
          }, 100);
        }
      } catch (err) {
        console.error('Failed to load zones:', err);
      }
    };

    loadZones();
  }, [cameraId, isImageLoaded, scaleFactor]);

  // Get current zone color
  const getCurrentColor = useCallback(() => {
    return ZONE_TYPE_COLORS[currentZoneType] || '#00FFCE';
  }, [currentZoneType]);

  // Redraw canvas
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    drawAllPolygons(ctx, img, masterPoints, masterColors, masterZoneTypes);

    if (points.length > 0 && !editMode) {
      drawCurrentPolygon(ctx, points, getCurrentColor(), img);
    }
  }, [masterPoints, masterColors, masterZoneTypes, points, editMode, getCurrentColor]);

  // Load image
  const handleLoadImage = useCallback(async (file) => {
    try {
      setImageFile(file);
      const img = await loadImageFromFile(file);
      imgRef.current = img;

      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return false;

      const scale = calculateImageScale(img, container.offsetWidth, container.offsetHeight, 0.95);
      setScaleFactor(scale);

      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      setImageDimensions({ width: img.width, height: img.height });

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      setIsImageLoaded(true);

      // Upload image to persist it
      if (cameraId) {
        setUploading(true);
        try {
          await camerasAPI.updateImage(cameraId, file);
          setSuccess('Image uploaded!');
          setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
          console.error('Image upload failed:', err);
        } finally {
          setUploading(false);
        }
      }

      return true;
    } catch (error) {
      console.error('Error loading image:', error);
      setError(error.message);
      return false;
    }
  }, [cameraId]);

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleLoadImage(file);
    }
  };

  // Add point
  const handleAddPoint = useCallback((x, y) => {
    if (drawMode === DRAW_MODES.LINE_CROSSING) {
      if (lineCrossingStep === LINE_CROSSING_STEPS.DIRECTION) {
        if (directionLine.length < 2) {
          setDirectionLine([...directionLine, [x, y]]);
          if (directionLine.length === 1) {
            setLineCrossingStep(LINE_CROSSING_STEPS.CROSSING);
          }
        }
      } else if (lineCrossingStep === LINE_CROSSING_STEPS.CROSSING) {
        if (crossingLine.length < 2) {
          setCrossingLine([...crossingLine, [x, y]]);
          if (crossingLine.length === 1) {
            const flatPoints = [
              directionLine[0][0], directionLine[0][1],
              directionLine[1][0], directionLine[1][1],
              crossingLine[0][0], crossingLine[0][1],
              x, y
            ];
            setMasterPoints([...masterPoints, flatPoints]);
            setMasterColors([...masterColors, getCurrentColor()]);
            setMasterZoneTypes([...masterZoneTypes, currentZoneType]);
            setMasterZoneIds([...masterZoneIds, null]); // New zone, no ID yet
            setDirectionLine([]);
            setCrossingLine([]);
            setLineCrossingStep(LINE_CROSSING_STEPS.DIRECTION);
          }
        }
      }
    } else if (drawMode === DRAW_MODES.LINE) {
      if (points.length < 2) {
        const newPoints = [...points, [x, y]];
        setPoints(newPoints);
        if (newPoints.length === 2) {
          setMasterPoints([...masterPoints, newPoints]);
          setMasterColors([...masterColors, getCurrentColor()]);
          setMasterZoneTypes([...masterZoneTypes, currentZoneType]);
          setMasterZoneIds([...masterZoneIds, null]); // New zone, no ID yet
          setPoints([]);
        }
      }
    } else {
      // Polygon mode
      const newPoints = [...points, [x, y]];
      setPoints(newPoints);

      // Auto-close polygon after 4 points
      if (newPoints.length === 4) {
        setMasterPoints([...masterPoints, newPoints]);
        setMasterColors([...masterColors, getCurrentColor()]);
        setMasterZoneTypes([...masterZoneTypes, currentZoneType]);
        setMasterZoneIds([...masterZoneIds, null]); // New zone, no ID yet
        setPoints([]);
      }
    }
    redrawCanvas();
  }, [drawMode, lineCrossingStep, directionLine, crossingLine, points, masterPoints, masterColors, masterZoneTypes, masterZoneIds, currentZoneType, getCurrentColor, redrawCanvas]);

  // Close polygon
  const handleClosePolygon = useCallback(() => {
    if (points.length > 2 && drawMode === DRAW_MODES.POLYGON) {
      setMasterPoints([...masterPoints, points]);
      setMasterColors([...masterColors, getCurrentColor()]);
      setMasterZoneTypes([...masterZoneTypes, currentZoneType]);
      setMasterZoneIds([...masterZoneIds, null]); // New zone, no ID yet
      setPoints([]);
      redrawCanvas();
    }
  }, [points, masterPoints, masterColors, masterZoneTypes, masterZoneIds, drawMode, currentZoneType, getCurrentColor, redrawCanvas]);

  // Move point (edit mode)
  const handleMovePoint = useCallback((polygonIndex, pointIndex, x, y) => {
    const newMasterPoints = [...masterPoints];
    newMasterPoints[polygonIndex][pointIndex] = [x, y];
    setMasterPoints(newMasterPoints);
    redrawCanvas();
  }, [masterPoints, redrawCanvas]);

  // Select point
  const handleSelectPoint = useCallback((polygonIndex, pointIndex) => {
    setSelectedPolygonIndex(polygonIndex);
    setSelectedPointIndex(pointIndex);
  }, []);

  // Get constrained coordinates
  const getConstrainedCoords = useCallback((x, y) => {
    if (points.length === 0) return [x, y];
    const lastPoint = points[points.length - 1];
    return constrainToAngle(lastPoint, x, y, RADIANS_PER_45_DEGREES);
  }, [points]);

  // Draw temporary preview
  const handleDrawTemporary = useCallback((pts, color, currentX, currentY, mode) => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    drawAllPolygons(ctx, img, masterPoints, masterColors, masterZoneTypes);

    if (pts.length > 0) {
      drawCurrentPolygon(ctx, pts, color, img);
      drawPreviewLine(ctx, pts, currentX, currentY, color, mode, img);
    }
  }, [masterPoints, masterColors, masterZoneTypes]);

  // Draw line crossing progress
  const handleDrawLineCrossingProgress = useCallback((dirLine, crossLine, step) => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    drawLineCrossingWorkInProgress(ctx, dirLine, crossLine, step, img);
  }, []);

  // Zoom
  const handleZoom = useCallback((delta) => {
    setScaleFactor(prev => {
      const newScale = prev - (delta * SCALE_SPEED);
      return Math.max(MIN_SCALE_FACTOR, Math.min(MAX_SCALE_FACTOR, newScale));
    });
  }, []);

  // Update scale factor effect
  useEffect(() => {
    if (!isImageLoaded) return;

    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    canvas.width = img.width * scaleFactor;
    canvas.height = img.height * scaleFactor;

    redrawCanvas();
  }, [scaleFactor, isImageLoaded, redrawCanvas]);

  // Set zone type
  const handleSetZoneType = useCallback((zoneType) => {
    setCurrentZoneType(zoneType);
    setDrawMode(ZONE_TYPE_DRAW_MODES[zoneType]);
    if (zoneType === ZONE_TYPES.LINE_CROSSING) {
      setLineCrossingStep(LINE_CROSSING_STEPS.DIRECTION);
      setDirectionLine([]);
      setCrossingLine([]);
    }
  }, []);

  // Undo last point
  const handleUndo = useCallback(() => {
    if (points.length > 0) {
      setPoints(points.slice(0, -1));
    } else if (masterPoints.length > 0) {
      setMasterPoints(masterPoints.slice(0, -1));
      setMasterColors(masterColors.slice(0, -1));
      setMasterZoneTypes(masterZoneTypes.slice(0, -1));
    }
    redrawCanvas();
  }, [points, masterPoints, masterColors, masterZoneTypes, redrawCanvas]);

  // Clear all
  const handleClearAll = useCallback(() => {
    if (confirm('Clear all zones?')) {
      setPoints([]);
      setMasterPoints([]);
      setMasterColors([]);
      setMasterZoneTypes([]);
      setDirectionLine([]);
      setCrossingLine([]);
      setSelectedZoneIndex(-1);
      redrawCanvas();
    }
  }, [redrawCanvas]);

  const handleDeleteZone = useCallback(async (index) => {
    const zoneId = masterZoneIds[index];

    // If zone has an ID (persisted in DB), delete via API
    if (zoneId) {
      try {
        await zonesAPI.delete(zoneId);
      } catch (err) {
        console.error('Failed to delete zone:', err);
        setError('Failed to delete zone');
        return;
      }
    }

    const newMasterPoints = masterPoints.filter((_, i) => i !== index);
    const newMasterColors = masterColors.filter((_, i) => i !== index);
    const newMasterZoneTypes = masterZoneTypes.filter((_, i) => i !== index);
    const newMasterZoneIds = masterZoneIds.filter((_, i) => i !== index);

    setMasterPoints(newMasterPoints);
    setMasterColors(newMasterColors);
    setMasterZoneTypes(newMasterZoneTypes);
    setMasterZoneIds(newMasterZoneIds);
    setSelectedZoneIndex(-1);

    // Redraw canvas
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (canvas && img) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      drawAllPolygons(ctx, img, newMasterPoints, newMasterColors, newMasterZoneTypes);
    }
  }, [masterPoints, masterColors, masterZoneTypes, masterZoneIds]);

  // Save zones
  const handleSaveZones = useCallback(async () => {
    if (!cameraId || masterPoints.length === 0) {
      setError('No zones to save');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const zonesToSave = masterPoints.map((zonePoints, index) => {
        const zoneType = masterZoneTypes[index];
        const imgWidth = imageDimensions.width;
        const imgHeight = imageDimensions.height;

        // Handle different zone point formats and normalize to 0-1 ratio
        let pointsArray;
        if (zoneType === ZONE_TYPES.LINE_CROSSING) {
          // Line crossing: flat array [x1d, y1d, x2d, y2d, x1c, y1c, x2c, y2c]
          // Convert to normalized objects: {x: ratio, y: ratio}
          if (Array.isArray(zonePoints) && !Array.isArray(zonePoints[0])) {
            pointsArray = [];
            for (let i = 0; i < zonePoints.length; i += 2) {
              if (i + 1 < zonePoints.length) {
                pointsArray.push({
                  x: zonePoints[i] / imgWidth,
                  y: zonePoints[i + 1] / imgHeight
                });
              }
            }
          } else {
            pointsArray = [];
          }
        } else if (zoneType === ZONE_TYPES.DIRECTION_DETECTION) {
          // Direction: flat array [x1, y1, x2, y2]
          if (Array.isArray(zonePoints) && !Array.isArray(zonePoints[0])) {
            pointsArray = [];
            for (let i = 0; i < zonePoints.length; i += 2) {
              if (i + 1 < zonePoints.length) {
                pointsArray.push({
                  x: zonePoints[i] / imgWidth,
                  y: zonePoints[i + 1] / imgHeight
                });
              }
            }
          } else {
            pointsArray = [];
          }
        } else {
          // ROI Filtering, Overcrowding: array of [x, y] pairs
          pointsArray = Array.isArray(zonePoints[0])
            ? zonePoints.map(([x, y]) => ({
                x: x / imgWidth,
                y: y / imgHeight
              }))
            : [];
        }

        return {
          camera_id: cameraId,
          name: `Zone ${index + 1}`,
          zone_type: zoneType,
          points: pointsArray,
          zone_index: index,
          enabled: true,
        };
      });

      // Delete all existing zones first
      for (const zoneId of masterZoneIds) {
        if (zoneId) {
          await zonesAPI.delete(zoneId);
        }
      }

      // Create new zones and collect their IDs
      const newZoneIds = [];
      for (const zone of zonesToSave) {
        const result = await zonesAPI.create(zone);
        newZoneIds.push(result.zoneId);
      }

      // Update state with new zone IDs
      setMasterZoneIds(newZoneIds);

      setSuccess('Zones saved!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save zones');
    } finally {
      setSaving(false);
    }
  }, [cameraId, masterPoints, masterZoneTypes, masterZoneIds, imageDimensions]);

  // Export config
  const handleExportConfig = useCallback(() => {
    if (masterPoints.length === 0) {
      setError('No zones to export');
      return;
    }

    exportDeepStreamConfig(
      masterPoints,
      masterZoneTypes,
      imageDimensions.width,
      imageDimensions.height,
      cameraName,
      cameraFormId,
      'config_nvdsanalytics.txt'
    );
    setSuccess('Config exported!');
    setTimeout(() => setSuccess(''), 3000);
  }, [masterPoints, masterZoneTypes, imageDimensions, cameraName, cameraFormId]);

  // Import config from text
  const handleImportFromText = useCallback(() => {
    try {
      if (!importText.trim()) {
        setError('Please enter zone configuration data');
        return;
      }

      const zones = parseDeepStreamConfig(importText);

      if (zones.length === 0) {
        setError('No zones found in configuration');
        return;
      }

      // Convert imported zones to canvas format
      const newMasterPoints = [];
      const newMasterColors = [];
      const newMasterZoneTypes = [];

      zones.forEach(zone => {
        newMasterZoneTypes.push(zone.type);
        newMasterColors.push(ZONE_TYPE_COLORS[zone.type] || '#00FFCE');
        newMasterPoints.push(zone.points);
      });

      setMasterPoints(newMasterPoints);
      setMasterColors(newMasterColors);
      setMasterZoneTypes(newMasterZoneTypes);

      redrawCanvas();

      setShowImportDialog(false);
      setImportText('');
      setSuccess(`Imported ${zones.length} zone${zones.length !== 1 ? 's' : ''}!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to parse configuration');
      setTimeout(() => setError(''), 5000);
    }
  }, [importText, redrawCanvas]);

  // Import config from file (legacy)
  const handleImportConfig = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const zones = await loadConfigFromFile(file);

      if (zones.length === 0) {
        setError('No zones found in config file');
        return;
      }

      // Convert imported zones to canvas format
      const newMasterPoints = [];
      const newMasterColors = [];
      const newMasterZoneTypes = [];

      zones.forEach(zone => {
        newMasterZoneTypes.push(zone.type);
        newMasterColors.push(ZONE_TYPE_COLORS[zone.type] || '#00FFCE');
        newMasterPoints.push(zone.points);
      });

      setMasterPoints(newMasterPoints);
      setMasterColors(newMasterColors);
      setMasterZoneTypes(newMasterZoneTypes);

      redrawCanvas();

      setSuccess(`Imported ${zones.length} zone${zones.length !== 1 ? 's' : ''}!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to import config');
      setTimeout(() => setError(''), 5000);
    }

    // Reset the file input
    if (configInputRef.current) {
      configInputRef.current.value = '';
    }
  }, [redrawCanvas]);

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Compact Top Bar */}
      <div className="border-b bg-card">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/cameras')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <div>
              <span className="font-semibold">{cameraName}</span>
              <span className="text-xs text-muted-foreground ml-2">
                {masterPoints.length} zones
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isImageLoaded && (
              <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            )}
            {isImageLoaded && (
              <Button size="sm" variant="outline" onClick={() => setShowImportDialog(true)}>
                <FileUp className="h-4 w-4 mr-2" />
                Import
              </Button>
            )}
            <Button size="sm" onClick={handleSaveZones} disabled={saving || masterPoints.length === 0}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleExportConfig} disabled={masterPoints.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="px-4 pb-2">
            <div className="p-2 border border-destructive bg-destructive/10 text-destructive rounded-md text-xs">
              {error}
            </div>
          </div>
        )}
        {success && (
          <div className="px-4 pb-2">
            <div className="p-2 border border-green-500 bg-green-50 text-green-700 rounded-md text-xs flex items-center">
              <CheckCircle2 className="h-3 w-3 mr-2" />
              {success}
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Minimal Left Toolbar */}
        <div className="border-r bg-card w-16 flex flex-col items-center py-4 gap-2">
          {/* Zone Types */}
          {Object.entries(ZONE_TYPE_LABELS).map(([type, label]) => {
            const IconComponent = ZONE_TYPE_ICONS[type];
            return (
              <Button
                key={type}
                variant={currentZoneType === type ? 'default' : 'ghost'}
                size="icon"
                className="h-12 w-12"
                onClick={() => handleSetZoneType(type)}
                title={label}
              >
                <IconComponent className="h-5 w-5" />
              </Button>
            );
          })}

          <Separator className="my-2 w-8" />

          {/* Tools */}
          <Button
            variant={!editMode ? 'default' : 'ghost'}
            size="icon"
            className="h-10 w-10"
            onClick={() => setEditMode(false)}
            title="Draw Mode"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant={editMode ? 'default' : 'ghost'}
            size="icon"
            className="h-10 w-10"
            onClick={() => setEditMode(true)}
            title="Edit Mode"
          >
            <Edit3 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            onClick={handleUndo}
            title="Undo"
          >
            <Undo className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 text-destructive"
            onClick={handleClearAll}
            title="Clear All"
          >
            <Trash2 className="h-4 w-4" />
          </Button>

          <Separator className="my-2 w-8" />

          {/* Zoom */}
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            onClick={() => handleZoom(-1)}
            title="Zoom In"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            onClick={() => handleZoom(1)}
            title="Zoom Out"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>

          <div className="flex-1" />

          {/* Angle Constraint Toggle */}
          <Button
            variant={constrainAngles ? 'default' : 'ghost'}
            size="icon"
            className="h-10 w-10"
            onClick={() => setConstrainAngles(!constrainAngles)}
            title={constrainAngles ? 'Angle constraint ON' : 'Angle constraint OFF'}
          >
            <Grid3x3 className="h-4 w-4" />
          </Button>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 relative bg-muted/20">
          <CanvasContainer
            canvasRef={canvasRef}
            containerRef={containerRef}
            scaleFactor={scaleFactor}
            isImageLoaded={isImageLoaded}
            cursor={cursor}
            drawMode={drawMode}
            editMode={editMode}
            points={points}
            masterPoints={masterPoints}
            selectedPointIndex={selectedPointIndex}
            selectedPolygonIndex={selectedPolygonIndex}
            constrainAngles={constrainAngles}
            currentZoneType={currentZoneType}
            lineCrossingStep={lineCrossingStep}
            directionLine={directionLine}
            crossingLine={crossingLine}
            getCurrentColor={getCurrentColor}
            getConstrainedCoords={getConstrainedCoords}
            onAddPoint={handleAddPoint}
            onMovePoint={handleMovePoint}
            onSelectPoint={handleSelectPoint}
            onLoadImage={handleLoadImage}
            onZoom={handleZoom}
            onDrawTemporary={handleDrawTemporary}
            onDrawLineCrossingProgress={handleDrawLineCrossingProgress}
            onClosePolygon={handleClosePolygon}
            onCoordinatesChange={setCoordinates}
            setCursorStyle={setCursor}
          />

          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <input
            ref={configInputRef}
            type="file"
            accept=".txt,.conf"
            onChange={handleImportConfig}
            className="hidden"
          />

          {/* Image Resolution Display (Top Right) */}
          {isImageLoaded && (
            <div className="absolute top-4 right-4 bg-card/90 backdrop-blur-sm border rounded-md px-3 py-2 text-xs">
              <div className="font-semibold text-muted-foreground mb-1">Original Resolution</div>
              <div className="font-mono">{imageDimensions.width} × {imageDimensions.height}</div>
            </div>
          )}

          {/* Coordinates Display (Bottom Right) */}
          {isImageLoaded && (
            <div className="absolute bottom-4 right-4 bg-card/90 backdrop-blur-sm border rounded-md px-3 py-1.5 text-xs font-mono">
              {coordinates.x}, {coordinates.y}
            </div>
          )}

        </div>
      </div>

      {/* Bottom Zone Bar */}
      {isImageLoaded && masterPoints.length > 0 && (
        <div className="border-t bg-card">
          <div className="px-4 py-2">
            <div className="flex items-center gap-2 overflow-x-auto">
              <span className="text-xs text-muted-foreground font-semibold flex-shrink-0">
                Zones ({masterPoints.length}):
              </span>
              {masterPoints.map((zonePoints, index) => {
                const IconComponent = ZONE_TYPE_ICONS[masterZoneTypes[index]];
                return (
                  <div
                    key={index}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md border flex-shrink-0 cursor-pointer transition-colors ${
                      selectedZoneIndex === index
                        ? 'bg-primary/10 border-primary'
                        : 'bg-card hover:bg-muted'
                    }`}
                    onClick={() => setSelectedZoneIndex(index)}
                  >
                    {IconComponent && <IconComponent className="h-3 w-3" style={{ color: masterColors[index] }} />}
                    <span className="text-xs font-medium">
                      Zone {index + 1}
                    </span>
                    <Badge variant="secondary" className="text-[10px] h-4 px-1">
                      {ZONE_TYPE_LABELS[masterZoneTypes[index]]}
                    </Badge>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-5 w-5 hover:bg-destructive/20 hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteZone(index);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Import Zones Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Import Zone Configuration</DialogTitle>
            <DialogDescription>
              Paste DeepStream zone configuration below
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="import-zones">Zone Configuration</Label>
              <Textarea
                id="import-zones"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder={`# ROI Filtering
roi-RF=100;200;500;200;500;400;100;400

# Line Crossing (direction: x1d;y1d;x2d;y2d, crossing: x1c;y1c;x2c;y2c)
line-crossing-Entry=300;100;600;100;300;400;600;400

# Overcrowding
roi-OC=150;250;450;250;450;450;150;450`}
                className="font-mono text-xs h-96"
              />
              <p className="text-xs text-muted-foreground">
                Supports DeepStream configuration format for:
                <br />
                <strong>ROI Filtering:</strong> roi-RF=x1;y1;x2;y2;x3;y3...
                <br />
                <strong>Overcrowding:</strong> roi-OC=x1;y1;x2;y2;x3;y3...
                <br />
                <strong>Line Crossing:</strong> line-crossing-Name=x1d;y1d;x2d;y2d;x1c;y1c;x2c;y2c
                <br />
                <strong>Direction Detection:</strong> direction-Name=x1;y1;x2;y2
              </p>
            </div>
            <div className="bg-muted p-3 rounded text-xs space-y-2">
              <p className="font-semibold">Example - ROI Filtering Zone:</p>
              <p className="font-mono">roi-RF=100;200;500;200;500;400;100;400</p>
              <p className="text-muted-foreground">Creates a rectangular zone with 4 corners</p>

              <p className="font-semibold mt-3">Example - Line Crossing:</p>
              <p className="font-mono">line-crossing-Entry=300;100;600;100;300;400;600;400</p>
              <p className="text-muted-foreground">Direction line (300,100 to 600,100), Crossing line (300,400 to 600,400)</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleImportFromText} disabled={!importText.trim()}>
              Import Zones
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
